import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { createShop, fetchCategories } from '@/store/slices/shopsSlice';
import { fetchCategories as fetchProductCategories } from '@/store/slices/productsSlice';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';

const CreateShopPage: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { categories } = useSelector((state: RootState) => state.products);
  const { isLoading, error } = useSelector((state: RootState) => state.shops);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: 1,
    is_public: true,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    // Fetch categories for shop creation
    dispatch(fetchProductCategories());
  }, [dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) errors.name = 'Shop name is required';
    if (formData.name.length < 2) errors.name = 'Shop name must be at least 2 characters';
    if (!formData.description.trim()) errors.description = 'Shop description is required';
    if (formData.description.length < 10) errors.description = 'Description must be at least 10 characters';
    if (!formData.category_id) errors.category_id = 'Please select a category';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Creating shop with data:', formData);
    
    if (validateForm()) {
      try {
        await dispatch(createShop(formData)).unwrap();
        // Redirect to seller dashboard after successful creation
        // Set seller role in localStorage so dashboard shows seller view
        localStorage.setItem('selectedRole', 'seller');
        window.location.href = '/dashboard?role=seller';
      } catch (error) {
        console.error('Shop creation error:', error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary-100 rounded-full">
                <BuildingStorefrontIcon className="h-8 w-8 text-primary-600" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Create Your Shop</h1>
            <p className="mt-2 text-gray-600">
              Set up your shop to start selling on RecyclerBin
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        {/* Shop Creation Form */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <form className="space-y-6" onSubmit={onSubmit} noValidate>
            {/* Shop Name */}
            <Input
              label="Shop Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={formErrors.name}
              placeholder="Enter your shop name"
              autoComplete="organization"
            />

            {/* Shop Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shop Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Describe what your shop sells and what makes it special"
              />
              {formErrors.description && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.description}
                </p>
              )}
            </div>

            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shop Category
              </label>
              <select
                name="category_id"
                value={formData.category_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {formErrors.category_id && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.category_id}
                </p>
              )}
            </div>

            {/* Shop Visibility */}
            <div className="flex items-center">
              <input
                id="is_public"
                name="is_public"
                type="checkbox"
                checked={formData.is_public}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_public" className="ml-2 block text-sm text-gray-900">
                Make my shop public (visible to customers)
              </label>
            </div>

            {/* Submit Button */}
            <div className="flex space-x-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => window.location.href = '/dashboard/seller/shop'}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                loading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Creating Shop...' : 'Create Shop'}
              </Button>
            </div>
          </form>
        </div>

        {/* Information Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">What happens next?</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Your shop will be created and ready for product listings</li>
            <li>• You can start adding products immediately</li>
            <li>• Admin will review your shop for approval</li>
            <li>• Once approved, your shop will be visible to customers</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CreateShopPage;
